import * as React from 'react';

import {
  enumCurrencyCode,
  enumDeductionApplicationType,
} from '@rematter/frontend-lib-api-client';
import { enumLoadWeightType } from '@rematter/modules/trade/types/load';
import { toMoney } from '@rematter/modules/trade/utils/money';
import {
  Col,
  ConsoleTouchDialog,
  Grid,
  humanReadableShortPricingUnit,
  Money,
  Row,
  TouchNumberField,
  TouchTextField,
  weightToPounds,
} from '@rematter/ui-web';
import Big from 'big.js';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { LucidePackage } from 'lucide-react';

import {
  $addDeduction,
  $deleteEntry,
  $itemsInterfaceEntries,
  $itemsInterfaceFacilityDefaultUnit,
  $updateDeduction,
  $updateLineItem,
  getLineItemById,
  getLineItemDeductions,
} from '../_state';
import { AddDeductionField } from '../fields/AddDeductionField';
import { useDialogModalStyle } from '../hooks/useDialogModalStyle';
import {
  DEDUCTION_GRID_CLASS,
  DialogItemRowDeduction,
} from '../rows/DialogItemRowDeduction';
import {
  calculateDeductionPercentage,
  calculateSiblingPercentageTotal,
  calculateWeightFromPercentage,
  clampPercentage,
} from '../utils/deductionPercentage';

import { TouchDialogHeader } from './TouchDialogHeader';

import {
  $closeRawMaterialDetails,
  $rawMaterialDetailsState,
} from '~/modules/trade/_state/materialsInterface.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import {
  convertWeightForPricing,
  toWeight,
} from '~/modules/trade/utils/weight';
import { SelectableDeductionType } from '~/shared/components/select-fields/SelectDeductionTypeTouch';
import { useTheme, useUIPreferences } from '~/shared/hooks';

type MaterialDetailsView = 'details' | 'photos';

const FORMAT_WEIGHT: Intl.NumberFormatOptions = {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
};

const TABS: MaterialDetailsView[] = ['details', 'photos'];

//
// ⚛️ ⚛️ ⚛️ EDIT MATERIAL DETAILS DIALOG ⚛️ ⚛️ ⚛️
//
export const EditMaterialDetailsDialog = () => {
  // Translations
  const t = useTranslations();
  const labels = t.scaleTicketPro.materialDetails;

  // States
  const [view, setView] = React.useState<MaterialDetailsView>('details');
  const [editedNetWeight, setEditedNetWeight] = React.useState<number | null>(
    null,
  );

  // Atoms
  const defaultUnit = useAtomValue($itemsInterfaceFacilityDefaultUnit);
  const entriesState = useAtomValue($itemsInterfaceEntries);
  const [rawDetailsState, setRawDetailsState] = useAtom(
    $rawMaterialDetailsState,
  );
  const closeRawDetails = useSetAtom($closeRawMaterialDetails);
  const updateLineItem = useSetAtom($updateLineItem);
  const addDeduction = useSetAtom($addDeduction);
  const updateDeduction = useSetAtom($updateDeduction);
  const deleteEntry = useSetAtom($deleteEntry);

  // Other data hooks
  const { effectiveColorScheme } = useTheme();
  const { touchModeEnabled } = useUIPreferences();
  const modalStyle = useDialogModalStyle();

  const { isOpen, lineItemId, grossWeight, tareWeight, notes } =
    rawDetailsState;

  // Memoized values
  const lineItemDeductions = React.useMemo(
    () => getLineItemDeductions(entriesState.items, lineItemId),
    [entriesState.items, lineItemId],
  );

  const lineItem = React.useMemo(
    () => getLineItemById(entriesState.items, lineItemId),
    [entriesState.items, lineItemId],
  );

  const isTaggedItem = lineItem?.type === 'Tagged' && !!lineItem?.tagId;

  const netWeight =
    editedNetWeight !== null ? editedNetWeight : grossWeight - tareWeight;

  const summary = React.useMemo(() => {
    const unitPrice = new Big(lineItem?.unitPrice?.amount ?? 0);
    const pricingUnit = lineItem?.pricingUnit ?? defaultUnit;
    const calculatedNetWeight = netWeight > 0 ? netWeight : 0;
    const netWeightPounds = weightToPounds(
      toWeight(calculatedNetWeight, defaultUnit),
    );
    const isEach = pricingUnit === 'Each';
    const subtotal = isEach
      ? unitPrice.times(calculatedNetWeight)
      : unitPrice.times(convertWeightForPricing(netWeightPounds, pricingUnit));

    const deductionsTotal = lineItemDeductions.reduce(
      (sum, d) => sum.plus(d.calculatedAmount?.amount ?? 0),
      new Big(0),
    );

    const currencyCode =
      lineItem?.unitPrice?.currencyCode ?? enumCurrencyCode.USD;

    return {
      subtotal: subtotal.toNumber(),
      deductionsTotal: deductionsTotal.toNumber(),
      itemTotal: subtotal.minus(deductionsTotal).toNumber(),
      currencyCode,
    };
  }, [lineItem, lineItemDeductions, netWeight, defaultUnit]);

  // Callbacks
  const applyChanges = React.useCallback(
    (overrides: {
      grossValue?: number;
      tareValue?: number;
      netValue?: number;
      notes?: string;
    }) => {
      if (!lineItemId) return;

      const g = overrides.grossValue ?? grossWeight;
      const ta = overrides.tareValue ?? tareWeight;
      const net =
        overrides.netValue !== undefined ? overrides.netValue : g - ta;

      updateLineItem(lineItemId, {
        weights: {
          [enumLoadWeightType.Gross]: g ? toWeight(g, defaultUnit) : null,
          [enumLoadWeightType.Tare]: ta ? toWeight(ta, defaultUnit) : null,
          [enumLoadWeightType.Net]: net > 0 ? toWeight(net, defaultUnit) : null,
        },
        notes: (overrides.notes ?? notes) || null,
      });
    },
    [lineItemId, grossWeight, tareWeight, notes, defaultUnit, updateLineItem],
  );

  const handleAddDeduction = React.useCallback(
    (deduction: {
      typeId: string;
      typeName: string;
      amount: number;
      isWeightBased: boolean;
    }) => {
      if (!lineItemId) return;
      addDeduction({ ...deduction, lineItemId });
    },
    [lineItemId, addDeduction],
  );

  const handleUpdateDeductionType = React.useCallback(
    (
      deductionId: string,
      option?: string,
      entity?: SelectableDeductionType,
    ) => {
      if (option && entity) {
        updateDeduction(deductionId, {
          deductionTypeId: option,
          deductionTypeName: entity.name,
          applicationType:
            entity.applicationType === enumDeductionApplicationType.Weight
              ? 'Weight'
              : 'Price',
        });
      }
    },
    [updateDeduction],
  );

  const handleDeductionWeightChange = React.useCallback(
    (deductionId: string, value: number) => {
      const parentNet = netWeight > 0 ? netWeight : 0;
      const siblingTotal = calculateSiblingPercentageTotal(
        lineItemDeductions,
        deductionId,
        parentNet,
      );
      const newPercentage = calculateDeductionPercentage(value, parentNet);

      updateDeduction(deductionId, {
        weightValue: toWeight(value, defaultUnit),
        percentage: clampPercentage(newPercentage, siblingTotal),
      });
    },
    [defaultUnit, netWeight, lineItemDeductions, updateDeduction],
  );

  const handleDeductionPercentageChange = React.useCallback(
    (deductionId: string, newPercentage: number) => {
      const parentNet = netWeight > 0 ? netWeight : 0;
      const siblingTotal = calculateSiblingPercentageTotal(
        lineItemDeductions,
        deductionId,
        parentNet,
      );
      const clamped = clampPercentage(newPercentage, siblingTotal);

      updateDeduction(deductionId, {
        percentage: clamped,
        weightValue: toWeight(
          calculateWeightFromPercentage(clamped, parentNet),
          defaultUnit,
        ),
      });
    },
    [defaultUnit, netWeight, lineItemDeductions, updateDeduction],
  );

  const handleDeductionPriceChange = React.useCallback(
    (deductionId: string, value: number) => {
      updateDeduction(deductionId, {
        priceValue: toMoney({
          amount: value || 0,
          currencyCode: enumCurrencyCode.USD,
        }),
      });
    },
    [updateDeduction],
  );

  /** Reset to details tab when the dialog opens */
  React.useEffect(() => {
    if (isOpen) {
      setView('details');
      setEditedNetWeight(null);
    }
  }, [isOpen]);

  return (
    <ConsoleTouchDialog
      touchModeEnabled={touchModeEnabled}
      effectiveColorScheme={effectiveColorScheme}
      isOpen={isOpen}
      onOpenChange={closeRawDetails}
      className="@EditMaterialDetailsDialog"
      dialogClassName="h-full w-full max-w-full"
      classNameHeader="!pb-0"
      contentStyle={modalStyle}
      header={
        <TouchDialogHeader
          icon={<LucidePackage size={16} />}
          iconColor="primary"
          title={labels.title}
          tabs={TABS.map((tab) => ({
            key: tab,
            label: labels.tabs[tab],
          }))}
          selectedTab={view}
          onTabChange={(key) => setView(key as MaterialDetailsView)}
        />
      }
    >
      {view === 'details' && (
        <Col gap4>
          <Grid cols3 gap4>
            <Col gap1>
              <span className="text-xs font-medium text-neutral/60 uppercase">
                {t.scaleTicketPro.columns.grossWeight}
              </span>
              <TouchNumberField
                aria-label={t.scaleTicketPro.columns.grossWeight}
                value={grossWeight}
                isDisabled={isTaggedItem}
                onChange={(v) => {
                  setRawDetailsState((prev) => ({
                    ...prev,
                    grossWeight: v,
                  }));
                  applyChanges({ grossValue: v });
                }}
                formatOptions={FORMAT_WEIGHT}
                touchModeEnabled
                suffix={humanReadableShortPricingUnit(defaultUnit)}
                className="h-16 text-2xl"
              />
            </Col>
            <Col gap1>
              <span className="text-xs font-medium text-neutral/60 uppercase">
                {t.scaleTicketPro.columns.tareWeight}
              </span>
              <TouchNumberField
                aria-label={t.scaleTicketPro.columns.tareWeight}
                value={tareWeight}
                isDisabled={isTaggedItem}
                onChange={(v) => {
                  setRawDetailsState((prev) => ({
                    ...prev,
                    tareWeight: v,
                  }));
                  applyChanges({ tareValue: v });
                }}
                formatOptions={FORMAT_WEIGHT}
                touchModeEnabled
                suffix={humanReadableShortPricingUnit(defaultUnit)}
                className="h-16 text-2xl"
              />
            </Col>
            <Col gap1>
              <span className="text-xs font-medium text-neutral/60 uppercase">
                {t.scaleTicketPro.columns.netWeight}
              </span>
              <TouchNumberField
                aria-label={t.scaleTicketPro.columns.netWeight}
                value={netWeight > 0 ? netWeight : 0}
                isDisabled={!isTaggedItem}
                onChange={(v) => {
                  setEditedNetWeight(v);
                  applyChanges({ netValue: v });
                }}
                formatOptions={FORMAT_WEIGHT}
                touchModeEnabled
                suffix={humanReadableShortPricingUnit(defaultUnit)}
                className="h-16 text-2xl"
              />
            </Col>
          </Grid>

          <TouchTextField
            label={t.scaleTicketPro.freightDetails.fields.notes}
            placeholder={t.scaleTicketPro.addMixedMaterial.notesPlaceholder}
            value={notes}
            onChange={(v) => {
              setRawDetailsState((prev) => ({ ...prev, notes: v }));
              applyChanges({ notes: v });
            }}
            touchModeEnabled
            rows={4}
          />

          <Col gap1>
            <div className={DEDUCTION_GRID_CLASS}>
              <span className="text-xs font-semibold text-neutral/60 uppercase">
                {labels.deductions}
              </span>
              <div />
              <span className="w-full text-xs font-medium text-neutral/60 uppercase">
                {t.scaleTicketPro.columns.netWeight}
              </span>
              <span className="w-full text-end text-xs font-medium text-neutral/60 uppercase">
                {labels.subtotal}
              </span>
              <div />
            </div>

            {lineItemDeductions.map((deduction) => (
              <DialogItemRowDeduction
                key={deduction.id}
                deduction={deduction}
                netWeight={netWeight}
                defaultUnit={defaultUnit}
                touchModeEnabled={touchModeEnabled}
                labels={labels}
                emptyDescriptionLabel={
                  t.scaleTicketPro.itemsInterface.emptyDescription
                }
                onUpdateType={handleUpdateDeductionType}
                onPercentageChange={handleDeductionPercentageChange}
                onWeightChange={handleDeductionWeightChange}
                onPriceChange={handleDeductionPriceChange}
                onDelete={deleteEntry}
              />
            ))}

            <div className={DEDUCTION_GRID_CLASS}>
              <div className="mt-2 max-w-40">
                <AddDeductionField
                  onAddDeduction={handleAddDeduction}
                  label={labels.addDeduction}
                  buttonSize="md"
                  placement="bottom left"
                  variant="text"
                />
              </div>
              <div />
              <div />
              <Row xBetween className="px-2">
                <Money
                  amount={summary.deductionsTotal}
                  currencyCode={summary.currencyCode}
                  className="w-full text-sm font-semibold text-failure"
                />
              </Row>
              <div />
            </div>
          </Col>

          <Grid cols3 gap4 className="rounded-lg bg-neutral/5 px-4 py-3">
            <Col gap1 xCenter>
              <span className="text-xs font-semibold text-neutral/50 uppercase">
                {labels.subtotal}
              </span>
              <Row xBetween>
                <Money
                  amount={summary.subtotal}
                  currencyCode={summary.currencyCode}
                  className="text-lg font-semibold"
                />
              </Row>
            </Col>
            <Col gap1 xCenter>
              <span className="text-xs font-semibold text-failure uppercase">
                {labels.deductionsTotal}
              </span>
              <Row xBetween>
                <Money
                  amount={summary.deductionsTotal}
                  currencyCode={summary.currencyCode}
                  className="text-lg font-semibold text-failure"
                />
              </Row>
            </Col>
            <Col gap1 xCenter>
              <span className="text-xs font-semibold text-success uppercase">
                {labels.itemTotal}
              </span>
              <Row xBetween>
                <Money
                  amount={summary.itemTotal}
                  currencyCode={summary.currencyCode}
                  className="text-lg font-semibold text-success"
                />
              </Row>
            </Col>
          </Grid>
        </Col>
      )}

      {view === 'photos' && (
        <Row xCenter yCenter className="h-full text-sm text-neutral/50">
          {labels.noPhotos}
        </Row>
      )}
    </ConsoleTouchDialog>
  );
};
